import type {
    BaseSyntheticEvent,
    ComponentType,
    FC,
    KeyboardEvent,
    LazyExoticComponent,
    ReactElement,
    ReactNode
} from "react"
import { createElement, lazy, startTransition } from "react"

import { AxiosError, HttpStatusCode } from "axios"
import eq from "lodash-es/eq"
import find from "lodash-es/find"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isEqual from "lodash-es/isEqual"
import isUndefined from "lodash-es/isUndefined"
import reduceRight from "lodash-es/reduceRight"
import split from "lodash-es/split"
import toLower from "lodash-es/toLower"
import { DateTime } from "luxon"
import { batch } from "react-redux"
import { matchPath } from "react-router-dom"
import { JSX } from "react/jsx-runtime"
import { v4 as createUuidV4 } from "uuid"

import type { TTFunction } from "$/i18n/i18n"

import {
    EVerticalProgressBarColorStatus,
    EVerticalProgressBarFillLevel
} from "@/3514/components/shared/progress-bars/vertical-progress-bar/vertical-progress-bar.types"
// Deep imports, not the "@/3514/components" barrel. utils/index.ts re-exports this module, and that
// barrel leads back here, so pulling it from inside utils means the components barrel is entered
// while utils is still initialising — every helper below then reads as undefined
// (`TypeError: (0, utils_1.isSafariBrowser) is not a function`). The three enums live in type
// modules with no imports at all; Spinner is only dereferenced inside a function body, so its own
// dependency on utils is resolved long after both modules are loaded.
import { Spinner } from "@/3514/components/shared/spinner"
import { ESpinnerSize } from "@/3514/components/shared/spinner/spinner.types"
import { ERoute } from "@/3514/constants"
import {
    EModulePhaseProgressStatus,
    EModulePhaseTrackStatus,
    ESessionTypeName,
    type TSessionTypeNameDto
} from "@/3514/types"
import { ParticipantUrls } from "@/constants"
import { CoachCalendarStepUrls } from "@/pages/coach-settings/constants"
// Deep, not the "@/shared/rich-text-renderer" barrel. That barrel's first line re-exports
// rich-text-renderer-component, which imports the "@/3514/hooks" barrel -> use-logout ->
// entities/participant.header -> adapters -> back into "@/3514/utils". Since utils/index.ts reaches
// this module on its own line 5, the barrel gets re-entered at line 2 while still initialising and
// every helper declared below reads as undefined
// (`TypeError: (0, utils_1.getTransitionTwClasses) is not a function`).
// The two modules below are leaves: -types has type-only imports, -utils imports one lodash function.
// The `lazy(() => import(...))` below keeps using the barrel on purpose — a dynamic import resolves
// when the component first renders, long after every module here has finished initialising.
import {
    ERichTextRendererMode,
    type TRichTextRendererProps
} from "@/shared/rich-text-renderer/rich-text-renderer-types"
import { richTextRendererUtils } from "@/shared/rich-text-renderer/rich-text-renderer-utils"
import type { TEmptyAsyncCallback, TEmptyCallback } from "@/shared/types/functions"

import IntrinsicElements = JSX.IntrinsicElements

const RichTextRenderer: LazyExoticComponent<FC<TRichTextRendererProps>> = lazy(
    (): Promise<{ default: FC<TRichTextRendererProps> }> => import("@/shared/rich-text-renderer")
)

/**
 * @deprecated since fram-er-motion is installed
 */
function getScaleAnimationCssString(
    config: { identifier: string; scaleTo?: number; durationInMs?: number } = {
        identifier: String()
    }
): string {
    const defaultConfig = { scaleTo: 0.9, durationInMs: 250 }

    const renderConfig = { ...defaultConfig, ...config }

    return `
      .${renderConfig.identifier}-enter {
        opacity: 0;
        transform: scale(${renderConfig.scaleTo});
      }
      .${renderConfig.identifier}-enter-active {
        opacity: 1;
        transform: scale(1);
        transition: opacity ${renderConfig.durationInMs}ms, transform ${renderConfig.durationInMs}ms;
      }
      .${renderConfig.identifier}-exit {
        opacity: 1;
        transform: scale(1);
      }
      .${renderConfig.identifier}-exit-active {
        opacity: 0;
        transform: scale(${renderConfig.scaleTo});
        transition: opacity ${renderConfig.durationInMs}ms, transform ${renderConfig.durationInMs}ms;
      }
    `
}

/**
 * @deprecated since fram-er-motion is installed
 */
function getSlideAnimationCssString(
    config: { identifier: string; translateYInPx?: number; durationInMs?: number } = {
        identifier: String()
    }
): string {
    const defaultConfig = { translateYInPx: 20, durationInMs: 250 }

    const renderConfig = { ...defaultConfig, ...config }

    return `
      .${renderConfig.identifier}-enter {
        opacity: 0;
        transform: translateY(${renderConfig.translateYInPx}px);
      }
      .${renderConfig.identifier}-enter-active {
        opacity: 1;
        transform: translateY(0);
        transition: opacity ${renderConfig.durationInMs}ms, transform ${renderConfig.durationInMs}ms;
      }
      .${renderConfig.identifier}-exit {
        opacity: 1;
        transform: translateY(0);
      }
      .${renderConfig.identifier}-exit-active {
        opacity: 0;
        transform: translateY(${renderConfig.translateYInPx}px);
        transition: opacity ${renderConfig.durationInMs}ms, transform ${renderConfig.durationInMs}ms;
      }
    `
}

/**
 * @deprecated since fram-er-motion is installed
 */
function getBlurAnimationCssString(
    config: { identifier: string; blurFrom?: number; durationInMs?: number; withVisibilityChange?: boolean } = {
        identifier: String()
    }
): string {
    const defaultConfig = { blurFrom: 100, durationInMs: 250, withVisibilityChange: false }

    const renderConfig = { ...defaultConfig, ...config }

    return `
      .${renderConfig.identifier} {
        will-change: filter, opacity;
      }
      .${renderConfig.identifier}-enter {
        opacity: 0;
        visibility: ${renderConfig.withVisibilityChange ? "hidden" : "visible"};
        filter: blur(${renderConfig.blurFrom}px);
      }
      .${renderConfig.identifier}-enter-active {
        opacity: 1;
        filter: blur(0);
        visibility: ${renderConfig.withVisibilityChange ? "visible" : "visible"};
        transition: opacity ${renderConfig.durationInMs}ms, filter ${renderConfig.durationInMs}ms;
      }
      .${renderConfig.identifier}-exit {
        opacity: 1;
        filter: blur(0);
        visibility: ${renderConfig.withVisibilityChange ? "visible" : "visible"};
      }
      .${renderConfig.identifier}-exit-active {
        opacity: 0;
        visibility: ${renderConfig.withVisibilityChange ? "hidden" : "visible"};
        filter: blur(${renderConfig.blurFrom}px);
        transition: opacity ${renderConfig.durationInMs}ms, visibility ${renderConfig.durationInMs}ms; filter ${renderConfig.durationInMs}ms;
      }
    `
}

// clsx polyfill
type _TClassValue = string | number | boolean | null | undefined | _IClassDictionary | _IClassArray
interface _IClassArray extends Array<_TClassValue> {}
interface _IClassDictionary {
    [key: string]: any
}
function buildCssClassName(...args: _TClassValue[]): string {
    const classes: string[] = []

    // eslint-disable-next-line no-restricted-syntax
    for (const arg of args) {
        if (!arg) {
            continue
        }

        const argType: _TClassValue = typeof arg

        if (argType === "string" || argType === "number") {
            classes.push(String(arg))
        } else if (Array.isArray(arg)) {
            const inner: string = buildCssClassName(...arg)
            if (inner) {
                classes.push(inner)
            }
        } else if (argType === "object") {
            // eslint-disable-next-line no-restricted-syntax
            for (const key in arg as _IClassDictionary) {
                if (Object.prototype.hasOwnProperty.call(arg, key) && (arg as _IClassDictionary)[key]) {
                    classes.push(key)
                }
            }
        }
    }

    return classes.join(" ")
}

function getTransitionTwClasses(
    variant: "colors" | "all" | string = "all",
    durationInMs = 200,
    type: "ease-linear" | "ease-in" | "ease-out" | "ease-in-out" = "ease-in-out"
): string {
    return `transition-${variant} duration-${durationInMs} ${type}`
}

function getLinkDecorationTwClasses(
    params: {
        color?: "gray" | "blue" | "accent"
        isGroup?: boolean
        isDefaultDecorTransparent?: boolean
        offset?: 1 | 3
    } = {
        color: "blue",
        isGroup: false,
        isDefaultDecorTransparent: false,
        offset: 3
    }
): string {
    const _isGray: boolean = params.color === "gray"
    const _isBlue: boolean = params.color === "blue"
    const _isAccent: boolean = params.color === "accent"

    const _isOffset1: boolean = params.offset === 1
    const _isOffset3: boolean = params.offset === 3

    return buildCssClassName(
        "!underline",
        "![outline-none]",
        "!decoration-[1px]",
        { "!underline-offset-[1px]": _isOffset1 },
        { "!underline-offset-[3px]": _isOffset3 },
        { "decoration-transparent": params.isDefaultDecorTransparent },
        { "!decoration-gray-200": !params.isDefaultDecorTransparent && _isGray },
        { "!decoration-blue-200": !params.isDefaultDecorTransparent && _isBlue },
        { "!decoration-accent": !params.isDefaultDecorTransparent && _isAccent },
        [
            params.isGroup
                ? [
                      { "group-focus:!underline group-focus:![outline-none] group-hover:!underline": true },
                      { "group-focus:!underline-offset-[3px] group-hover:!underline-offset-[3px]": _isOffset1 },
                      { "group-focus:!underline-offset-[5px] group-hover:!underline-offset-[5px]": _isOffset3 },
                      { "group-focus:decoration-gray-200 group-hover:decoration-gray-200": _isGray },
                      { "group-focus:decoration-blue-200 group-hover:decoration-blue-200": _isBlue },
                      { "group-focus:decoration-accent group-hover:decoration-accent": _isAccent }
                  ]
                : [
                      { "focus:!underline focus:![outline-none] hover:!underline": true },
                      { "focus:underline-offset-[3px] hover:!underline-offset-[3px]": _isOffset1 },
                      { "focus:underline-offset-[5px] hover:!underline-offset-[5px]": _isOffset3 },
                      { "focus:decoration-gray-200 hover:decoration-gray-200": _isGray },
                      { "focus:decoration-blue-200 hover:decoration-blue-200": _isBlue },
                      { "focus:decoration-accent hover:decoration-accent": _isAccent }
                  ]
        ],
        getTransitionTwClasses("all", 200)
    )
}

function getCssIdentifierFromFileName(fileName: string, postfix?: string): string {
    const name: string = fileName
        .replace(/([A-Z])/g, " $1")
        .trim()
        .split(" ")
        .map((word: string): string => word[0].toLowerCase())
        .join("")

    return isEmpty(postfix) ? name : `${name}-${postfix}`
}

function renderRichContent({
    identifier,
    classes,
    editorClasses,
    allowedTags
}: {
    identifier: string
    classes: string
    editorClasses?: string
    allowedTags?: (keyof IntrinsicElements)[]
}): (content: string) => ReactElement {
    const fallbackLoader: ReactNode = createElement(Spinner, { size: ESpinnerSize.Small })

    // eslint-disable-next-line react/display-name
    return (content: string): ReactElement =>
        createElement(RichTextRenderer, {
            content,
            key: identifier,
            mode: ERichTextRendererMode.View,
            isEditorForceSkipped: !richTextRendererUtils.isStringMarkdown(content),
            fallbackLoader,
            cssConfig: {
                editorClassName: editorClasses,
                classNameIdentifier: identifier,
                classNamesToOverride: classes
            },
            ...(!isEmpty(allowedTags) && { allowedHtmlTags: allowedTags })
        })
}

// simple whole props comparison
function propsAreEqual<TProps>(prev: TProps, next: TProps): boolean {
    return isEqual(prev, next)
}

type _TGuardHOC<P> = (component: ComponentType<P>) => ComponentType<P>

function composeGuards<T>(...guards: _TGuardHOC<T>[]): (BaseComponent: ComponentType<T>) => ComponentType<T> {
    return (BaseComponent: ComponentType<T>): ComponentType<T> =>
        reduceRight<_TGuardHOC<T>, ComponentType<T>>(
            guards,
            (acc: ComponentType<T>, guard: _TGuardHOC<T>): ComponentType<T> => guard(acc),
            BaseComponent
        )
}

function getVerticalProgressBarFillLevelEnumByModulePhaseProgressStatusEnum(
    status: EModulePhaseProgressStatus
): EVerticalProgressBarFillLevel {
    switch (status) {
        case EModulePhaseProgressStatus.FirstChapterCompleted:
            return EVerticalProgressBarFillLevel.OnePieceFilled
        case EModulePhaseProgressStatus.SecondChapterCompleted:
            return EVerticalProgressBarFillLevel.TwoPiecesFilled
        case EModulePhaseProgressStatus.ThirdChapterCompleted:
            return EVerticalProgressBarFillLevel.ThreePiecesFilled
        case EModulePhaseProgressStatus.NotAvailable:
        default:
            return EVerticalProgressBarFillLevel.NotFilled
    }
}

function getVerticalProgressBarColorStatusEnumByModulePhaseTrackStatusEnum(
    status: EModulePhaseTrackStatus,
    isDefaultNotStarted?: boolean
): EVerticalProgressBarColorStatus {
    if (isDefaultNotStarted) return EVerticalProgressBarColorStatus.NotStarted

    switch (status) {
        case EModulePhaseTrackStatus.OnTime:
            return EVerticalProgressBarColorStatus.InTime
        case EModulePhaseTrackStatus.Late:
            return EVerticalProgressBarColorStatus.Expired
        case EModulePhaseTrackStatus.NotStarted:
        default:
            return EVerticalProgressBarColorStatus.NotStarted
    }
}

function getTimeZoneAbbreviation(): string {
    return DateTime.now().toFormat("ZZZZ")
}

function getTimeZoneIanaAbbreviation(): string {
    const abbreviation: string = getTimeZoneAbbreviation()
    const iana: string | null = DateTime.now().zoneName

    return iana ?? abbreviation
}

function getGeneralQueryErrorMessage(error: AxiosError | Error | null, t: TTFunction): string {
    if (error instanceof AxiosError) {
        if (eq(error.status, HttpStatusCode.BadRequest)) return t("coachSide.queryErrorBoundary.request.400")
        if (eq(error.status, HttpStatusCode.Forbidden)) return t("coachSide.queryErrorBoundary.request.403")
        if (eq(error.status, HttpStatusCode.NotFound)) return t("coachSide.queryErrorBoundary.request.404")
        if (eq(error.status, HttpStatusCode.InternalServerError)) return t("coachSide.queryErrorBoundary.request.500")
    }

    if (error instanceof Error) return error.message

    return t("coachSide.queryErrorBoundary.request.500")
}

const _actionExecutionTimes: WeakMap<Function, number> = new WeakMap<Function, number>()
function withPressHandler(
    action: TEmptyCallback | TEmptyAsyncCallback | ((e: BaseSyntheticEvent) => void)
): (e: BaseSyntheticEvent) => void {
    return (e: BaseSyntheticEvent): void => {
        const now: number = Date.now()
        const lastExecutionTime: number = _actionExecutionTimes.get(action) || 0

        if (now - lastExecutionTime < 100) return

        if ((e as KeyboardEvent).key === "Enter" || e.type === "click") {
            _actionExecutionTimes.set(action, now)
            action(e)
        }
    }
}

type _TBrowser = "chrome" | "firefox" | "safari" | "edge" | string

type _TBrowserDetection = {
    chrome: boolean
    firefox: boolean
    safari: boolean
    edge: boolean
}
function _detectBrowser(): _TBrowserDetection {
    if (isUndefined(window) || isUndefined(navigator))
        return { chrome: false, firefox: false, safari: false, edge: false }

    const uaString: string = navigator.userAgent || String("")

    const ua: string = toLower(uaString)

    return {
        chrome: includes<string>(ua, "chrome") && !includes<string>(ua, "edg") && !includes<string>(ua, "opr"),
        firefox: includes<string>(ua, "firefox"),
        safari: includes<string>(ua, "safari") && !includes<string>(ua, "chrome") && !includes<string>(ua, "chromium"),
        edge: includes<string>(ua, "edg")
    }
}

function _getBrowserVersion(browser: _TBrowser): string {
    const uaString: string = navigator.userAgent

    function _getVersion(regex: RegExp): string {
        const match: RegExpMatchArray = uaString.match(regex)
        return match ? match[1] : undefined
    }

    switch (browser) {
        case "chrome":
            return _getVersion(/chrome\/([\d.]+)/i)
        case "firefox":
            return _getVersion(/firefox\/([\d.]+)/i)
        case "safari":
            return _getVersion(/version\/([\d.]+)/i)
        case "edge":
            return _getVersion(/edg\/([\d.]+)/i)
        default:
            return undefined
    }
}

function _compareBrowserVersions(v1: string, v2: string): 1 | 0 | -1 {
    const a: number[] = split(v1, ".").map(Number)
    const b: number[] = split(v2, ".").map(Number)

    const length: number = Math.max(a.length, b.length)

    for (let i: number = 0; i < length; i++) {
        const x: number = a[i] ?? 0
        const y: number = b[i] ?? 0

        if (x > y) return 1
        if (x < y) return -1
    }

    return 0
}

function isBrowserVersionGreater(browser: _TBrowser, targetVersion: string): boolean {
    const detection: _TBrowserDetection = _detectBrowser()

    if (!detection[browser]) return true

    const currentVersion: string = _getBrowserVersion(browser)

    if (!currentVersion) return false

    return _compareBrowserVersions(currentVersion, targetVersion) > 0
}

function isFirefoxBrowser(): boolean {
    const _currentBrowser: _TBrowserDetection = _detectBrowser()

    return _currentBrowser.firefox
}

function isSafariBrowser(): boolean {
    const _currentBrowser: _TBrowserDetection = _detectBrowser()

    return _currentBrowser.safari
}

function getTranslatedSessionName(enumName: ESessionTypeName | string, t: TTFunction): string {
    switch (enumName) {
        case ESessionTypeName.BonusSession:
            return t("common.sessionName.bonus")
        case ESessionTypeName.InitialCoachingSession:
            return t("common.sessionName.initialCoaching")
        case ESessionTypeName.IndividualCoaching:
            return t("common.sessionName.individualCoaching")
        case ESessionTypeName.IndividualExecutiveOnboarding:
            return t("common.sessionName.individualExecutiveOnboarding")
        case ESessionTypeName.IndividualExecutiveCoaching:
            return t("common.sessionName.individualExecutiveCoaching")
        case ESessionTypeName.OnboardingSession:
            return t("common.sessionName.onboarding")
        case ESessionTypeName.GroupExecutiveOnboarding:
            return t("common.sessionName.groupExecutiveOnboarding")
        case ESessionTypeName.GroupExecutiveCoaching:
            return t("common.sessionName.groupExecutiveCoaching")
        case ESessionTypeName.SpotCoachingLab:
            return t("common.sessionName.spotCoaching")
        case ESessionTypeName.CoachingSession:
            return t("common.sessionName.coaching")
        default:
            return enumName
    }
}

const reduxBatch: (cb: TEmptyCallback) => void = batch

const idleCb: (cb: TEmptyCallback | TEmptyAsyncCallback, timeout?: number) => void = (
    cb: TEmptyCallback | TEmptyAsyncCallback,
    timeout = 0
): void =>
    isSafariBrowser() ? void setTimeout(cb, timeout) : void requestIdleCallback((): void => void cb(), { timeout })

const asyncCb: (cb: TEmptyCallback | TEmptyAsyncCallback, timeout?: number) => void = (
    cb: TEmptyCallback | TEmptyAsyncCallback,
    timeout = 0
): void => void setTimeout(cb, timeout)

const safeTransition: (cb: TEmptyCallback) => void = (cb: TEmptyCallback): void =>
    document.hidden ? cb() : startTransition((): void => cb())

function extractStringFromOneLevelNestedHTML(initialValue: string): string {
    return initialValue.match(/^<(\w+)[^>]*>(.*?)<\/\1>$/s)?.[2] ?? initialValue
}

function generateUuid(): string {
    return createUuidV4()
}

function getSessionNameEnumFromType(type: TSessionTypeNameDto): ESessionTypeName | string {
    switch (type) {
        case "Bonus Session":
            return ESessionTypeName.BonusSession
        case "Initial Coaching Session":
            return ESessionTypeName.InitialCoachingSession
        case "Individual Coaching":
            return ESessionTypeName.IndividualCoaching
        case "Individual Executive Onboarding":
            return ESessionTypeName.IndividualExecutiveOnboarding
        case "Individual Executive Coaching":
            return ESessionTypeName.IndividualExecutiveCoaching
        case "Coaching Session":
            return ESessionTypeName.CoachingSession
        case "Onboarding Session":
            return ESessionTypeName.OnboardingSession
        case "Group Executive Onboarding":
            return ESessionTypeName.GroupExecutiveOnboarding
        case "Group Executive Coaching":
            return ESessionTypeName.GroupExecutiveCoaching
        case "Spot Coaching Lab":
            return ESessionTypeName.SpotCoachingLab
        case null:
            return null
        default:
            return type
    }
}

function captureElementByAttribute(
    e: { target: EventTarget },
    callback: (index: string) => void | Promise<void>,
    attributeName: string = "data-index"
): void {
    const elem: HTMLElement = (e.target as HTMLElement).closest(`[${attributeName}]`)

    if (elem === null) return void 0

    return void callback(elem.getAttribute(`${attributeName}`))
}

enum ERoundDirection {
    Top = "top",
    Bottom = "bottom",
    Left = "left",
    Right = "right"
}

enum EAxisDirection {
    Vertical = "vertical",
    Horizontal = "horizontal"
}

function getCurrentRouteFromPathname(pathname: string): ERoute {
    return find<ERoute>(Object.values(ERoute), (r: ERoute): boolean => matchPath(r, pathname)?.pattern?.path === r)
}

function getCurrentLegacyCoachCalendarRouteFromPathname(pathname: string): CoachCalendarStepUrls {
    return find<CoachCalendarStepUrls>(
        Object.values(CoachCalendarStepUrls),
        (r: CoachCalendarStepUrls): boolean => matchPath(r, pathname)?.pattern?.path === r
    )
}

function getCurrentLegacyParticipantRouteFromPathname(pathname: string): ParticipantUrls {
    return find<ParticipantUrls>(
        Object.values(ParticipantUrls),
        (r: ParticipantUrls): boolean => matchPath(r, pathname)?.pattern?.path === r
    )
}

export { ERoundDirection, EAxisDirection }

export {
    getScaleAnimationCssString,
    getSlideAnimationCssString,
    getBlurAnimationCssString,
    buildCssClassName,
    getTransitionTwClasses,
    getLinkDecorationTwClasses,
    getCssIdentifierFromFileName,
    propsAreEqual,
    composeGuards,
    renderRichContent,
    getVerticalProgressBarFillLevelEnumByModulePhaseProgressStatusEnum,
    getVerticalProgressBarColorStatusEnumByModulePhaseTrackStatusEnum,
    getTimeZoneAbbreviation,
    getTimeZoneIanaAbbreviation,
    getGeneralQueryErrorMessage,
    withPressHandler,
    isFirefoxBrowser,
    isSafariBrowser,
    isBrowserVersionGreater,
    getTranslatedSessionName,
    reduxBatch,
    idleCb,
    asyncCb,
    safeTransition,
    extractStringFromOneLevelNestedHTML,
    generateUuid,
    getSessionNameEnumFromType,
    captureElementByAttribute,
    getCurrentRouteFromPathname,
    getCurrentLegacyCoachCalendarRouteFromPathname,
    getCurrentLegacyParticipantRouteFromPathname
}
